/* eslint-disable prefer-promise-reject-errors */
import firebase from '@/libs/firebase'
import store from '@/store'

let db = {
  board: null,
  history: null,
  objects: null,
  metadata: null
}

let storage = {
  board: null,
  images: null,
  files: null,
  snapshots: null
}

const listeners = {}

let uploadTask = null

let board = null
let localHistory = null
let metadata = null

const signInOptions = { allowAnonymous: true }

const dbRef = 'whiteboards'
const storageRef = 'attachments'

let restorePoint = {}

export default {
  ignoreKey: 'ignore',
  id: null,
  objectStatus: {},
  init: function (config) {
    return new Promise((resolve, reject) => {
      this.id = config.id
      this.objectStatus = config.objectStatus || {}

      db = {
        board: null,
        history: null,
        objects: null,
        metadata: null
      }

      storage = {
        board: null,
        images: null,
        files: null,
        snapshots: null
      }

      board = config.board
      localHistory = config.history

      restorePoint = { metadata: {}, objects: {} }

      firebase.projectBoard.signIn(signInOptions).then(() => {
        db.board = firebase.database().ref(dbRef).child(this.id)
        db.history = db.board.child('history')
        db.objects = db.board.child('objects')
        db.metadata = db.board.child('metadata')

        this.syncMetadata()

        storage.board = firebase.storage().ref(storageRef).child('' + this.id)
        storage.images = storage.board.child('images')
        storage.files = storage.board.child('files')
        storage.snapshots = storage.board.child('whiteboards')

        this.startListeners()

        resolve()
      }, () => {
        reject()
      })
    })
  },

  startListeners: function () {
    if (!listeners[this.id]) {
      listeners[this.id] = {}
    }

    if (!listeners[this.id].objects) {
      listeners[this.id].objects = {}
    }

    const objectListeners = listeners[this.id].objects

    if (!objectListeners.child_added) {
      objectListeners.child_added = db.objects.on('child_added', (snapshot) => {
        if (this.id === board.id) {
          this.objectStatus.loaded++
          board.queueObject(this.unpack(snapshot.val()))
        }
      })
    } else {
      db.objects.once('value', (snapshot) => {
        snapshot.forEach((child) => {
          this.objectStatus.loaded++
          board.queueObject(this.unpack(child.val()))
        })
      })
    }

    if (!objectListeners.child_changed) {
      objectListeners.child_changed = db.objects.on('child_changed', (snapshot) => {
        if (this.id === board.id) {
          board.updateObject(this.unpack(snapshot.val()))
        }
      })
    }

    if (!objectListeners.child_removed) {
      objectListeners.child_removed = db.objects.on('child_removed', (snapshot) => {
        if (this.id === board.id) {
          this.objectStatus.loaded--
          board.removeObject(this.unpack(snapshot.val()))
        }
      })
    }

    if (uploadTask) {
      uploadTask.cancel()
    }
  },

  stopListeners: function () {
    if (!listeners[this.id]) {
      return
    }

    if (listeners[this.id].objects) {
      Object.keys(listeners[this.id].objects).forEach((key) => {
        db.objects.off(key, listeners[this.id].objects[key])
      })

      listeners[this.id].objects = null
    }
  },

  initHistory: function (config) {
    this.id = config.id

    db.board = firebase.database().ref(dbRef).child(this.id)
    db.history = db.board.child('history')

    this.startHistoryListeners(config)
  },

  startHistoryListeners: function (config) {
    if (!listeners[this.id]) {
      listeners[this.id] = {}
    }

    if (!listeners[this.id].history) {
      listeners[this.id].history = {}
    }

    const historyListeners = listeners[this.id].history

    if (!historyListeners.child_added) {
      historyListeners.child_added = db.history.on('child_added', (snapshot) => {
        config.addCallback(snapshot.key, snapshot.val())
      })
    }

    if (!historyListeners.child_changed) {
      historyListeners.child_changed = db.history.on('child_changed', (snapshot) => {
        config.updateCallback(snapshot.key, snapshot.val())
      })
    }
  },

  stopHistoryListeners: function () {
    if (!listeners[this.id]) {
      return
    }

    if (listeners[this.id].history) {
      Object.keys(listeners[this.id].history).forEach((key) => {
        db.history.off(key, listeners[this.id].history[key])
      })

      listeners[this.id].history = null
    }
  },

  pack: function (object) {
    return JSON.stringify(object)
  },

  unpack: function (string) {
    return JSON.parse(string)
  },

  packAction: function (type, object) {
    return {
      type: type,
      object: object,
      user: store.getters.currentUser.uuid,
      date: new Date().toJSON()
    }
  },

  addToHistory: function (object, options = {}) {
    if (options.type !== 'redo') {
      object.actionKey = db.history.child(object.key).push(this.packAction(options.type, this.pack(object))).key
    }

    if (options.type === 'undo') {
      db.history.child(object.key).child(options.undoneAction).update({ undone: object.actionKey })
      localHistory.undo()
    } else if (options.type === 'redo') {
      db.history.child(object.key).child(options.redoneAction).update({ undone: null })
      db.history.child(object.key).child(options.undoAction).remove()
      localHistory.redo()
    } else {
      localHistory.add(object)
    }
  },

  getKey: function () {
    return db.history.push().key
  },

  add: function (object, options = {}) {
    if (object.key === this.ignoreKey) {
      return
    }
    if (!options.type) {
      options.type = 'add'
    }
    object.key = this.getKey()
    this.addToHistory(object, options)
    db.objects.child(object.key).set(this.pack(object))
    this.updateMetadata(1)
  },

  update: function (object, options = {}) {
    if (object.key === this.ignoreKey) {
      return
    }
    if (!options.type) {
      options.type = 'update'
    }
    this.addToHistory(object, options)
    db.objects.child(object.key).set(this.pack(object))
    this.updateMetadata(0)
  },

  remove: function (object, options = {}) {
    if (object.key === this.ignoreKey) {
      return
    }
    if (!options.type) {
      options.type = 'remove'
    }
    this.addToHistory(object, options)
    db.objects.child(object.key).remove()
    this.updateMetadata(-1)
  },

  clearBoard: function () {
    db.board.remove()
  },

  undo: function (action) {
    db.history.child(action.objectKey).limitToLast(10).once('value', (snapshot) => {
      const actions = snapshot.val()
      const keys = Object.keys(actions)
      let lastKey = keys.pop()
      let last = actions[lastKey]

      // skip all actions until we find last one that wasn't an undo or undone
      while (lastKey && (last.type === 'undo' || last.undone)) {
        lastKey = keys.pop()
        last = actions[lastKey]
      }

      // make sure user performed last action
      if (last.user !== store.getters.currentUser.uuid) {
        return
      }

      const options = {
        type: 'undo',
        undoneAction: lastKey
      }

      if (last.type === 'add') {
        this.remove(this.unpack(last.object), options)
      } else if (last.type === 'remove') {
        const object = this.unpack(last.object)
        object.key = action.objectKey
        this.update(object, options)
        this.updateMetadata(1)
      } else if (last.type === 'update' && keys.length >= 1) {
        const prev = actions[keys.pop()]
        this.update(this.unpack(prev.object), options)
      }
    })
  },

  redo: function (action) {
    db.history.child(action.objectKey).limitToLast(10).once('value', (snapshot) => {
      const actions = snapshot.val()
      const keys = Object.keys(actions)
      let undoKey = keys.pop()
      let undo = actions[undoKey]

      // find the first undo action by the user
      while (undoKey && (undo.type !== 'undo' || undo.user !== store.getters.currentUser.uuid)) {
        undoKey = keys.pop()
        undo = actions[undoKey]

        // still make sure user performed the action
        if (!undo || (undo.type !== 'undo' && !undo.undone)) {
          return
        }
      }

      // now try to find the action undone
      let lastKey = keys.pop()
      let last = actions[lastKey]

      // skip all actions until we find last one that wasn't an undo or undone
      while (lastKey && last.undone !== undoKey && (last.type === 'undo' || last.undone)) {
        lastKey = keys.pop()
        last = actions[lastKey]
      }

      if (lastKey !== action.actionKey || last.user !== store.getters.currentUser.uuid) {
        return
      }

      const options = {
        type: 'redo',
        redoneAction: lastKey,
        undoAction: last.undone
      }

      if (last.type === 'add') {
        this.update(this.unpack(last.object), options)
        this.updateMetadata(1)
      } else if (last.type === 'remove') {
        const object = this.unpack(last.object)
        object.key = action.objectKey
        object.actionKey = lastKey
        this.remove(object, options)
      } else if (last.type === 'update') {
        this.update(this.unpack(last.object), options)
      }
    })
  },

  upload: function (params) {
    const key = this.getKey()
    const task = params.bucket.child(key).put(params.file)

    this.monitorUpload(task, params.status, (snapshot) => {
      if (params.metadata) {
        params.bucket.child(key).updateMetadata(params.metadata)
      }
      snapshot.ref.getDownloadURL().then((downloadURL) => {
        params.callback(key, downloadURL)
      })
    })
  },

  uploadImage: function (file, status, callback) {
    return this.upload({ bucket: storage.images, file, status, callback })
  },

  uploadFile: function (file, status, callback) {
    const metadata = { contentDisposition: `inline; filename="${file.name}"` }
    return this.upload({ bucket: storage.files, file, status, callback, metadata })
  },

  uploadDataURI: function (uri, status, callback) {
    const isImage = uri.substring(0, 11) === 'data:image/'
    const key = this.getKey()
    const bucket = isImage ? storage.images : storage.files
    const task = bucket.child(key).putString(uri, 'data_url')

    this.monitorUpload(task, status, (snapshot) => {
      const file = { name: 'Untitled', type: snapshot.metadata.contentType, size: snapshot.metadata.size }
      snapshot.ref.getDownloadURL().then((downloadURL) => {
        callback(key, downloadURL, file)
      })
    })
  },

  uploadSnapshot: function (data, status, preview) {
    const metadata = {
      contentType: 'image/svg+xml'
    }

    const key = preview ? 'preview' : this.getKey()
    const task = storage.snapshots.child(key).putString(data, 'raw', metadata)

    this.monitorUpload(task, status)
  },

  monitorUpload: function (task, status, successCallback, errorCallback) {
    uploadTask = task

    task.on('state_changed', (snapshot) => {
      status.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
      status.paused = snapshot.state === firebase.storage.TaskState.PAUSED
      status.running = snapshot.state === firebase.storage.TaskState.RUNNING
    }, (error) => {
      uploadTask = null
      status.error = true
      if (errorCallback) {
        errorCallback(error)
      }
    }, () => {
      uploadTask = null
      setTimeout(() => {
        status.done = true
        task.snapshot.ref.getDownloadURL().then((downloadURL) => {
          status.url = downloadURL
          if (successCallback) {
            successCallback(task.snapshot)
          }
        })
      }, 500)
    })
  },

  hasData: function () {
    return (this.objectStatus.loaded || 0) > 0
  },

  numLoadedObjects: function () {
    return this.objectStatus.loaded
  },

  numExpectedObjects: function () {
    return this.objectStatus.expected
  },

  syncMetadata: function () {
    db.metadata.once('value', (snapshot) => {
      if (!snapshot.exists()) {
        db.metadata.set({ numObjects: 0, lastUpdated: Date.now() })
        this.objectStatus.expected = 0
      } else {
        metadata = snapshot.val()
        if (metadata.numObjects < 0) {
          metadata.numObjects = 0
          db.metadata.set({ numObjects: 0, lastUpdated: Date.now() })
        } else if (!metadata.lastUpdated) {
          db.metadata.set({ numObjects: metadata.numObjects, lastUpdated: Date.now() })
        }
        this.objectStatus.expected = (metadata.numObjects || 0)

        restorePoint.metadata.numObjects = metadata.numObjects
        restorePoint.metadata.lastUpdated = metadata.lastUpdated
      }

      db.objects.once('value', (snapshot) => {
        if (!snapshot.exists()) {
          if (this.objectStatus.expected > 0) {
            db.metadata.set({ numObjects: 0, lastUpdated: Date.now() })
            this.objectStatus.expected = 0
          }
        } else {
          const actual = snapshot.numChildren()
          if (this.objectStatus.expected !== actual) {
            db.metadata.set({ numObjects: actual, lastUpdated: Date.now() })
            this.objectStatus.expected = actual
          }
          restorePoint.objects = snapshot.val()
        }
      })
    })
  },

  updateMetadata: function (amount, objectStatus) {
    db.metadata.transaction(function (md) {
      if (md) {
        md.numObjects = (md.numObjects || 0) + amount
        md.lastUpdated = Date.now()
      } else {
        md = { numObjects: amount }
        md.lastUpdated = Date.now()
      }

      if (md.numObjects < 0) {
        md.numObjects = 0
      }

      if (objectStatus) {
        objectStatus.expected = md.numObjects
      }
      return md
    })
  },

  getDownloadURL: function (path) {
    return storage.board.child(path).getDownloadURL()
  },

  previewIsUpToDate: function (config) {
    this.id = config.id

    db.board = firebase.database().ref(dbRef).child(this.id)
    db.metadata = db.board.child('metadata')

    storage.board = firebase.storage().ref(storageRef).child('' + this.id)
    storage.snapshots = storage.board.child('whiteboards')

    return new Promise((resolve, reject) => {
      let lastUpdated = null
      let fileModified = null
      let isError = false
      let isEmpty = false

      const check = function () {
        if (isError || isEmpty) {
          return
        }

        if (!fileModified || !lastUpdated) {
          setTimeout(() => { check() }, 500)
          return
        }

        if (new Date(fileModified).getTime() < lastUpdated) {
          return reject()
        }

        return resolve()
      }

      check()

      db.metadata.once('value', (snapshot) => {
        let metadata = snapshot.val()
        if (!metadata) {
          metadata = {}
        }
        if (!metadata.lastUpdated) {
          metadata.lastUpdated = Date.now()
        }
        lastUpdated = metadata.lastUpdated

        if (metadata.numObjects === 0) {
          isEmpty = true
          reject('empty')
        }
      })

      storage.snapshots.child('preview').getMetadata().then((metadata) => {
        fileModified = metadata.timeCreated
      }).catch(() => {
        isError = true
        reject()
      })
    })
  },

  getPreviewURL: function (config) {
    return new Promise((resolve, reject) => {
      firebase.projectBoard.signIn(signInOptions).then(() => {
        this.previewIsUpToDate(config).then(() => {
          storage.snapshots.child('preview').getDownloadURL().then((url) => {
            resolve(url)
          }).catch(() => {
            reject(-1)
          })
        }).catch((response) => {
          reject(response === 'empty' ? 1 : 0)
        })
      }).catch(() => {
        reject(-1)
      })
    })
  },

  getItemFromHistory: function (key, callback) {
    return db.history.child(key).limitToLast(1).once('value', (snapshot) => {
      const actions = snapshot.val()
      if (actions) {
        const actionKey = Object.keys(actions).pop()
        callback(this.unpack(actions[actionKey].object))
      }
    })
  },

  restoreBoard: function () {
    if (!restorePoint.metadata) {
      return
    }

    if (Object.keys(restorePoint.metadata)) {
      db.metadata.set(restorePoint.metadata)
    }

    if (Object.keys(restorePoint.objects)) {
      db.objects.set(restorePoint.objects)
    }
  }
}
